"use client";

import { useMemo, useSyncExternalStore } from "react";
import Link from "next/link";
import { ArrowRight, History } from "@/components/icons";
import {
  RECENTLY_VIEWED_UPDATE_EVENT,
  readRecentlyViewed,
  type RecentlyViewedItem,
} from "@/lib/recently-viewed/storage";
import { ServerProductCard } from "@/components/features/listings/server-product-card";
import type { ServerProductCardItem } from "@/components/features/listings/product-card-display";
import { PRODUCT_CARD_SCROLL_SIZES } from "@/components/features/listings/image-sizes";
import { cn } from "@/lib/utils";

interface RecentlyViewedShelfProps {
  excludeProductId?: string;
  heading?: string;
  compact?: boolean;
  className?: string;
}

function toCardItem(item: RecentlyViewedItem): ServerProductCardItem {
  return {
    _id: item._id,
    title: item.title,
    buyerUnitPriceCents: item.buyerUnitPriceCents,
    primaryImageUrl: item.primaryImageUrl,
    slug: item.slug,
    publicIdentifier: item.publicIdentifier,
    cityName: item.cityName,
    stateAbbr: item.stateAbbr,
    originZip: item.originZip,
    intent: item.intent,
  };
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(RECENTLY_VIEWED_UPDATE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(RECENTLY_VIEWED_UPDATE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

let cachedSnapshot: RecentlyViewedItem[] = [];
let cachedSnapshotKey = "";
function getSnapshot(): RecentlyViewedItem[] {
  const fresh = readRecentlyViewed();
  const key = fresh.map((i) => `${i._id}:${i.viewedAt}`).join("|");
  if (key !== cachedSnapshotKey) {
    cachedSnapshot = fresh;
    cachedSnapshotKey = key;
  }
  return cachedSnapshot;
}
function getServerSnapshot(): RecentlyViewedItem[] {
  return cachedSnapshot;
}

function ShelfHeader({
  heading,
  compact,
}: {
  heading: string;
  compact: boolean;
}) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2
        id="recently-viewed-heading"
        className={cn(
          "flex items-center gap-2 font-semibold text-foreground",
          compact ? "text-base" : "text-lg sm:text-xl",
        )}
      >
        <History
          className={cn(
            "shrink-0 text-muted-foreground",
            compact ? "size-4" : "size-5",
          )}
          aria-hidden
        />
        {heading}
      </h2>
      <Link
        href="/listing"
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-action"
      >
        Keep browsing
        <ArrowRight className="size-4" />
      </Link>
    </div>
  );
}

export function RecentlyViewedShelf({
  excludeProductId,
  heading = "Recently viewed",
  compact = false,
  className,
}: RecentlyViewedShelfProps) {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const filtered = useMemo(
    () =>
      excludeProductId
        ? items.filter((i) => i._id !== excludeProductId)
        : items,
    [items, excludeProductId],
  );
  if (filtered.length === 0) return null;

  return (
    <section
      aria-labelledby="recently-viewed-heading"
      className={cn("w-full", className)}
    >
      <ShelfHeader heading={heading} compact={compact} />
      <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:thin]">
        {filtered.map((item) => (
          <div
            key={item._id}
            className={cn(
              "shrink-0",
              compact ? "w-[180px]" : "w-[240px] sm:w-[260px]",
            )}
          >
            <ServerProductCard
              item={toCardItem(item)}
              sizes={PRODUCT_CARD_SCROLL_SIZES}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
